import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleDetail, hotArticles } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articleDetail[Number(id)];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">瓜料未找到</h1>
          <p className="text-foreground-500 mb-6">这个瓜可能已经被吃完了，去看看其他新鲜瓜吧</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors"
          >
            <i className="ri-home-line"></i> 返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = hotArticles
    .filter((a) => a.id !== article.id)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt,
        "image": article.image,
        "author": {
          "@type": "Person",
          "name": article.author
        },
        "datePublished": article.publishedAt,
        "publisher": {
          "@type": "Organization",
          "name": "51吃瓜网",
          "url": "http://csxsjds.cn/"
        },
        "url": `http://csxsjds.cn/article/${article.id}`,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `http://csxsjds.cn/article/${article.id}`
        }
      }) }} />

      {/* Article Hero */}
      <section className="relative w-full h-[400px] md:h-[520px] flex items-end">
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
            title={article.title}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40"></div>
        </div>
        <div className="relative w-full max-w-[1280px] mx-auto px-4 md:px-6 pb-10 md:pb-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full bg-accent-500/20 backdrop-blur-sm border border-accent-500/30 text-accent-300 text-xs font-semibold"
              >
                {article.category}
              </Link>
              {article.isExclusive && (
                <span className="px-3 py-1 rounded-full bg-primary-500/80 text-background-50 text-xs font-bold">独家</span>
              )}
              <span className="text-white/60 text-xs">
                {new Date(article.publishedAt).toLocaleDateString("zh-CN", {
                  year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit"
                })}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white leading-snug mb-3">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-white/60 text-sm">
              <div className="w-6 h-6 rounded-full bg-accent-500/30 flex items-center justify-center">
                <i className="ri-user-line text-accent-300 text-xs"></i>
              </div>
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-white/30"></span>
              <span>
                <i className="ri-fire-line text-primary-400 mr-1"></i>
                {article.heat > 10000 ? `${(article.heat / 10000).toFixed(1)}万热度` : `${article.heat}热度`}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Main Content */}
          <div className="flex-1 max-w-none lg:max-w-[720px]">
            {/* Action Buttons */}
            <div className="flex items-center gap-3 mb-8 flex-wrap">
              <button className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-share-forward-line"></i> 分享瓜料
              </button>
              <button className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-300 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-bookmark-line"></i> 收藏瓜料
              </button>
              <button className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-300 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-alert-line"></i> 举报不实
              </button>
            </div>

            {/* Tags */}
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Content */}
            <div
              className="prose prose-base max-w-none text-foreground-700 leading-relaxed
                [&_p]:mb-4 [&_p]:text-[15px] [&_p]:leading-relaxed
                [&_strong]:text-foreground-900 [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Divider */}
            <div className="border-t border-background-200/70 my-8"></div>

            {/* Comments Section */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
                  热评区
                </span>
                <h2 className="text-xl font-heading font-bold text-foreground-900">
                  吃瓜群众 · 在线讨论
                </h2>
              </div>

              <div className="columns-1 md:columns-2 gap-4">
                {article.comments.map((comment) => (
                  <div key={comment.id} className="break-inside-avoid mb-4">
                    <div className="bg-background-100 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                          <img
                            src={comment.avatar}
                            alt={comment.username}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-foreground-900">{comment.username}</div>
                          <div className="text-xs text-foreground-400">{comment.handle}</div>
                        </div>
                        <span className="ml-auto text-xs text-foreground-400 whitespace-nowrap">{comment.time}</span>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        &ldquo;{comment.content}&rdquo;
                      </p>
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-background-200/60">
                        <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer">
                          <i className="ri-thumb-up-line"></i> {comment.likes}
                        </button>
                        <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer">
                          <i className="ri-chat-1-line"></i> 回复
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="lg:w-[300px] flex-shrink-0">
            <div className="sticky top-24">
              <div className="bg-background-100 rounded-xl p-5 mb-4">
                <h3 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i> 热度排行
                </h3>
                <div className="space-y-3">
                  {hotArticles.slice(0, 5).map((a, idx) => (
                    <Link
                      key={a.id}
                      to={`/article/${a.id}`}
                      className={`flex items-start gap-3 group ${a.id === article.id ? "opacity-50 pointer-events-none" : ""}`}
                    >
                      <span className={`text-sm font-bold flex-shrink-0 w-5 ${idx < 3 ? "text-primary-500" : "text-foreground-400"}`}>
                        {idx + 1}
                      </span>
                      <span className="text-xs text-foreground-600 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {a.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-14 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground-900">
              相关瓜料 <span className="text-primary-500">推荐</span>
            </h2>
            <p className="text-sm text-foreground-500 mt-1">你可能还对这些瓜感兴趣</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {relatedArticles.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.id}`}
                className="group cursor-pointer"
              >
                <div className="bg-background-50 rounded-xl overflow-hidden transition-all duration-300 group-hover:scale-[1.02]">
                  <div className="relative w-full h-[180px] overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      title={a.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  </div>
                  <div className="p-4">
                    <span className="text-[11px] text-primary-600 font-medium bg-primary-50 px-2 py-0.5 rounded-full">
                      {a.category}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug mt-2 group-hover:text-primary-600 transition-colors">
                      {a.title}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
